import React, { useState } from "react"

import { Form, Formik } from "formik"
import { gql, useMutation } from "urql"
import * as Yup from "yup"

import { Color } from "~tailwindui/types/enums"

import { AlertMessageBox, Button } from "~tailwindui/Basics"
import {
  CheckboxInput,
  Failures,
  SubmitButton,
  TextInput,
} from "~tailwindui/Form"
import Modal from "~tailwindui/Modal"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import classNames from "src/classNames"

import TechIssues from "./TechIssues"

const ValidationSchema = (isEarly: boolean) =>
  Yup.object().shape({
    earlyAcknowledgement: Yup.boolean().test(
      "early-acknowledgement",
      "Acknowledgement required",
      (earlyAcknowledgement: boolean) => {
        return isEarly ? earlyAcknowledgement === true : true
      }
    ),
    endedEarlyReason: Yup.string().test(
      "endedEarlyReason",
      "Please describe why the session was ended early.",
      (endedEarlyReason: string) => {
        return isEarly
          ? Boolean(endedEarlyReason && endedEarlyReason.trim())
          : true
      }
    ),
    sessionTechIssues: Yup.mixed()
      .when("hadTechIssues", {
        is: true,
        then: Yup.array().min(1, "Please select at least one option."),
      })
      .test(
        "sessionTechIssues",
        "Please provide required descriptions",
        sessionTechIssues => {
          const requiredDescriptions = sessionTechIssues.filter(issue =>
            ["cloudroom", "other"].includes(issue.issueType)
          )

          return requiredDescriptions.every(
            issue => issue.description && issue.description.length > 0
          )
        }
      ),
  })

type CompleteSessionModalProps = {
  session: {
    id: string
    endsAt: Date
    currentLessonResources: {
      id: string
      name: string
      isLast: boolean
      lessonTopic: {
        name: string
      }
    }[]
  }
}

const CompleteSessionModal: React.FC<CompleteSessionModalProps> = ({
  session: { id, endsAt, currentLessonResources },
}) => {
  const [isEarly, setIsEarly] = useState(() => {
    return new Date().getTime() < new Date(endsAt).getTime() - 1000 * 60
  })

  const refreshIsEarly = () => {
    setIsEarly(new Date().getTime() < new Date(endsAt).getTime() - 1000 * 60)
  }

  const formState = {
    completedLessonResourceIds: [],
    hadTechIssues: false,
    sessionTechIssues: [],
    earlyAcknowledgement: false,
    endedEarlyReason: "",
  }

  const [_, runMutation]: any[] = useMutation(completeSessionMutation)

  const handleSubmit = (values, actions) => {
    runMutation({ id: id, ...values })
      .then(
        response => {
          const { failures } = response.data.manuallyEndSession
          if (failures.length > 0) {
            actions.setStatus(failures[0].message)
          }
        },
        () => {
          actions.setStatus("An error occurred")
          actions.setSubmitting(false)
        }
      )
      .catch(() => {
        actions.setStatus("An error occurred")
        actions.setSubmitting(false)
      })
  }

  return (
    <Modal.Dialog
      buttonText="End Session"
      buttonClassNames={classNames("mb-1", buttonClassNames({}))}
      afterOpen={refreshIsEarly}
    >
      {closeModal => (
        <>
          <Modal.Header>End Session</Modal.Header>

          <Formik
            initialValues={formState}
            validationSchema={ValidationSchema(isEarly)}
            onSubmit={(values, actions) => handleSubmit(values, actions)}
            validateOnChange={false}
            validateOnBlur={false}
          >
            {({ status, values }) => (
              <Form className="flex max-h-[calc(70vh-90px)] flex-col">
                <Modal.Body>
                  <p className="text-lg">
                    This will send all participants to the post-session form.
                  </p>
                  {isEarly && (
                    <CheckboxInput
                      name="earlyAcknowledgement"
                      checkboxType="single"
                      label="I acknowledge that I am ending this session early."
                    />
                  )}
                  {values.earlyAcknowledgement && (
                    <TextInput
                      name="endedEarlyReason"
                      label="Please describe why:"
                    />
                  )}
                  {currentLessonResources.length > 0 && (
                    <div className="form-group">
                      <h3 className="mb-0 font-medium">Worksheets</h3>
                      <small className="form-text">Mark if completed</small>
                      <CheckboxInput
                        checkboxType="multi"
                        name="completedLessonResourceIds"
                        options={currentLessonResources.map(resource => ({
                          value: resource.id,
                          label: (
                            <>
                              <div>
                                {resource.lessonTopic.name}: {resource.name}
                              </div>
                              {resource.isLast && (
                                <div className="text-danger">
                                  This is the final worksheet for this chapter
                                </div>
                              )}
                            </>
                          ),
                        }))}
                      />
                    </div>
                  )}

                  <hr />
                  <TechIssues />
                </Modal.Body>

                <Modal.Footer>
                  <div className="flex flex-col">
                    {status && <AlertMessageBox>{status}</AlertMessageBox>}

                    <div className="flex justify-end space-x-2">
                      <Failures />
                      <Button color={Color.Red} onClick={closeModal}>
                        Cancel
                      </Button>
                      <SubmitButton>End the session</SubmitButton>
                    </div>
                  </div>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </>
      )}
    </Modal.Dialog>
  )
}

const completeSessionMutation = gql`
  mutation (
    $id: ID!
    $completedLessonResourceIds: [ID!]!
    $endedEarlyReason: String
    $earlyAcknowledgement: Boolean
    $sessionTechIssues: [SessionTechIssueInputObject!]!
  ) {
    manuallyEndSession(
      id: $id
      completedLessonResourceIds: $completedLessonResourceIds
      endedEarlyReason: $endedEarlyReason
      earlyAcknowledgement: $earlyAcknowledgement
      sessionTechIssues: $sessionTechIssues
    ) {
      airtutorsSession {
        id
      }
      failures {
        message
      }
    }
  }
`

export default CompleteSessionModal
